// ============================================
// Doc Module Service
//
// Doctor-facing workflow: generate synthetic case,
// build manual protocol, compare against AI,
// compute divergences, persist sessions.
// ============================================

import {
  collection, doc, setDoc, Timestamp,
} from 'firebase/firestore';
import { db } from '../firebase';
import type { BiomarkerResult, Protocol, FullReport } from '../../types';
import type { GeneratedPanel, LabValue } from '../../types/patientJourney';
import { generatePanel } from './panelEngine';
import { BIOMARKER_RANGES, type BiomarkerRange } from '../../data/biomarkerRanges';
import { runProtocolBuilder } from '../agents/protocolBuilder';
import { generateFullReport } from '../agents/reportGenerator';
import { claudeGenerate } from '../ai/providers/claude';
import { getManifest } from '../firestore/formularyService';
import { C_DOC_SESSIONS } from '../../constants/collections';

// ── Firestore collection ──

const docSessionsCol = () => collection(db, C_DOC_SESSIONS);

// ── Types ──

export interface DocCase {
  panel: GeneratedPanel;
  biomarkers: BiomarkerResult[];
  demographics: { age: number; sex: 'female' | 'male' };
  concerns: string[];
}

export interface DoctorProtocolItem {
  productId: string;
  name: string;
  dosage: string;
  timing: 'AM_ENERGY' | 'MIDDAY_ABSORPTION' | 'PM_RECOVERY';
  notes?: string;
}

export interface Divergence {
  type: 'product_selection' | 'dosage' | 'timing' | 'doctor_only' | 'ai_only';
  productName: string;
  doctorChoice: string;
  aiChoice: string;
  reasoning: string;
  doctorNotes?: string;
}

export interface DocSession {
  sessionId: string;
  timestamp: string;
  demographics: { age: number; sex: 'female' | 'male' };
  biomarkerCount: number;
  doctorProtocol: DoctorProtocolItem[];
  aiProtocol: Protocol;
  aiReport: FullReport;
  divergences: Divergence[];
  alignmentScore: number;
  doctorNotes: string;
}

// ── Helpers ──

/** Strip undefined values (Firestore rejects undefined). */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function stripUndefined(obj: any): any {
  if (obj === null || obj === undefined || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj.map(stripUndefined);
  const cleaned: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(obj)) {
    if (value !== undefined) {
      cleaned[key] = (value !== null && typeof value === 'object')
        ? stripUndefined(value)
        : value;
    }
  }
  return cleaned;
}

/**
 * Classify a biomarker value into a hemologixStatus based on ranges.
 */
function classifyHemologixStatus(
  value: number,
  range: BiomarkerRange,
): BiomarkerResult['hemologixStatus'] {
  // Critical: outside standard range by a significant margin
  if (value < range.standardMin * 0.8 || value > range.standardMax * 1.2) return 'critical';
  // Low / Elevated: outside standard range
  if (value < range.standardMin) return 'low';
  if (value > range.standardMax) return 'elevated';
  // Suboptimal: inside standard but outside functional
  if (value < range.functionalMin || value > range.functionalMax) return 'suboptimal';
  // Optimal: inside functional range
  return 'optimal';
}

/**
 * Build an interpretation string for a biomarker result.
 */
function buildInterpretation(
  value: number,
  range: BiomarkerRange,
  status: BiomarkerResult['hemologixStatus'],
): string {
  if (status === 'optimal') {
    return `${range.name} at ${value} ${range.unit} is within the functional optimal range (${range.functionalMin}-${range.functionalMax}).`;
  }
  if (status === 'suboptimal') {
    if (value < range.functionalMin) {
      return `${range.name} at ${value} ${range.unit} is below the functional optimal range (${range.functionalMin}-${range.functionalMax}) but within standard limits.`;
    }
    return `${range.name} at ${value} ${range.unit} is above the functional optimal range (${range.functionalMin}-${range.functionalMax}) but within standard limits.`;
  }
  if (status === 'low') {
    return `${range.name} at ${value} ${range.unit} is below the standard range (${range.standardMin}-${range.standardMax}).`;
  }
  if (status === 'elevated') {
    return `${range.name} at ${value} ${range.unit} is above the standard range (${range.standardMin}-${range.standardMax}).`;
  }
  // critical
  return `${range.name} at ${value} ${range.unit} is critically outside normal ranges and warrants immediate attention.`;
}

/**
 * Convert a LabValue from the panel engine to a full BiomarkerResult
 * by matching against BIOMARKER_RANGES.
 */
function labToBiomarkerResult(
  lab: LabValue,
  sex: 'female' | 'male',
): BiomarkerResult | null {
  // Find matching range — prefer gender-specific, fall back to 'any'
  const genderSpecific = BIOMARKER_RANGES.find(
    (r) => r.name === lab.biomarker && r.gender === sex,
  );
  const anyGender = BIOMARKER_RANGES.find(
    (r) => r.name === lab.biomarker && r.gender === 'any',
  );
  const range = genderSpecific ?? anyGender;

  if (!range) return null;

  const hemologixStatus = classifyHemologixStatus(lab.value, range);
  const standardStatus: 'normal' | 'abnormal' =
    lab.value >= range.standardMin && lab.value <= range.standardMax
      ? 'normal'
      : 'abnormal';

  return {
    biomarker: lab.biomarker,
    value: lab.value,
    unit: lab.unit,
    standardRange: { low: range.standardMin, high: range.standardMax },
    functionalRange: { low: range.functionalMin, high: range.functionalMax },
    eliteAverage: range.eliteAverage,
    standardStatus,
    hemologixStatus,
    category: range.category,
    interpretation: buildInterpretation(lab.value, range, hemologixStatus),
  };
}

/**
 * Map flagged biomarkers to patient-friendly concern phrases.
 */
const CONCERN_MAP: Record<string, Record<string, string>> = {
  'hormonal': {
    low: 'Fatigue, low energy, decreased libido',
    elevated: 'Mood swings, irritability, acne',
  },
  'thyroid': {
    low: 'Cold intolerance, weight gain, hair loss',
    elevated: 'Anxiety, rapid heartbeat, weight loss',
  },
  'metabolic': {
    low: 'Lightheadedness, weakness',
    elevated: 'Increased thirst, frequent urination, brain fog',
  },
  'nutritional': {
    low: 'Muscle weakness, bone pain, fatigue',
    elevated: 'Nausea, digestive issues',
  },
  'inflammatory': {
    low: 'General fatigue',
    elevated: 'Joint pain, chronic inflammation, poor recovery',
  },
  'cardiovascular': {
    low: 'Dizziness, cold extremities',
    elevated: 'Chest tightness, shortness of breath',
  },
  'hematological': {
    low: 'Fatigue, pallor, shortness of breath',
    elevated: 'Headaches, dizziness',
  },
};

function buildConcerns(biomarkers: BiomarkerResult[]): string[] {
  const seen = new Set<string>();
  const concerns: string[] = [];

  for (const bm of biomarkers) {
    if (bm.hemologixStatus === 'optimal') continue;

    const cat = bm.category || 'general';
    const direction = (bm.hemologixStatus === 'low' || bm.hemologixStatus === 'critical')
      ? 'low' : 'elevated';

    const concern = CONCERN_MAP[cat]?.[direction];
    if (concern && !seen.has(concern)) {
      seen.add(concern);
      concerns.push(concern);
    }
  }

  if (concerns.length === 0) {
    concerns.push('General wellness check — wants to optimize');
  }

  return concerns.slice(0, 5); // Cap at 5
}

// ============================================================
// PUBLIC API
// ============================================================

/**
 * Generate a synthetic patient case with biomarkers.
 */
export async function generateCase(
  tier: number,
  sex: 'female' | 'male',
  age: number,
): Promise<DocCase> {
  const panel = generatePanel({
    tier: tier as 1 | 2 | 3 | 4 | 5,
    demographics: {
      name: 'Synthetic Patient',
      age,
      sex: sex === 'female' ? 'Female' : 'Male',
      state: 'CA',
    },
    verticals: ['metabolic', 'hormonal', 'thyroid', 'nutritional'],
    healthProfile: 'mixed',
  });

  const biomarkers: BiomarkerResult[] = [];
  for (const lab of panel.labs) {
    const result = labToBiomarkerResult(lab, sex);
    if (result) biomarkers.push(result);
  }

  const concerns = buildConcerns(biomarkers);

  return {
    panel,
    biomarkers,
    demographics: { age, sex },
    concerns,
  };
}

/**
 * Run AI protocol builder + report generator in parallel.
 */
export async function runAIAnalysis(
  biomarkers: BiomarkerResult[],
  demographics: { age: number; sex: 'female' | 'male' },
): Promise<{ aiProtocol: Protocol; aiReport: FullReport }> {
  const [aiProtocol, aiReport] = await Promise.all([
    runProtocolBuilder(biomarkers, [], [], [], demographics.sex, false),
    generateFullReport(biomarkers, demographics, false),
  ]);

  return { aiProtocol, aiReport };
}

/**
 * Compare doctor's manual protocol against AI recommendations.
 * Returns an array of divergences.
 */
export function computeDivergences(
  doctorProtocol: DoctorProtocolItem[],
  aiProtocol: Protocol,
): Divergence[] {
  const divergences: Divergence[] = [];

  const normalize = (s: string) => s.toLowerCase().trim();

  // Build lookup maps
  const doctorMap = new Map<string, DoctorProtocolItem>();
  for (const item of doctorProtocol) {
    doctorMap.set(normalize(item.name), item);
  }

  const aiMap = new Map<string, typeof aiProtocol.supplements[0]>();
  for (const supp of aiProtocol.supplements) {
    aiMap.set(normalize(supp.name), supp);
  }

  // Check doctor items against AI
  for (const [normName, docItem] of doctorMap.entries()) {
    const aiMatch = aiMap.get(normName);

    if (!aiMatch) {
      // Doctor added something AI didn't recommend
      divergences.push({
        type: 'doctor_only',
        productName: docItem.name,
        doctorChoice: `${docItem.dosage} (${docItem.timing})`,
        aiChoice: 'Not recommended',
        reasoning: 'Doctor included this product but AI protocol did not.',
      });
      continue;
    }

    // Both have it — check dosage
    const docDosageNorm = normalize(docItem.dosage);
    const aiDosageNorm = normalize(aiMatch.dosage);
    if (docDosageNorm !== aiDosageNorm) {
      divergences.push({
        type: 'dosage',
        productName: docItem.name,
        doctorChoice: docItem.dosage,
        aiChoice: aiMatch.dosage,
        reasoning: `Dosage differs: doctor chose "${docItem.dosage}" vs AI "${aiMatch.dosage}".`,
      });
    }

    // Check timing
    if (docItem.timing !== aiMatch.timing) {
      divergences.push({
        type: 'timing',
        productName: docItem.name,
        doctorChoice: docItem.timing,
        aiChoice: aiMatch.timing,
        reasoning: `Timing differs: doctor chose ${docItem.timing} vs AI ${aiMatch.timing}.`,
      });
    }
  }

  // Check AI items not in doctor's protocol
  for (const [normName, aiSupp] of aiMap.entries()) {
    if (!doctorMap.has(normName)) {
      divergences.push({
        type: 'ai_only',
        productName: aiSupp.name,
        doctorChoice: 'Not included',
        aiChoice: `${aiSupp.dosage} (${aiSupp.timing})`,
        reasoning: aiSupp.rationale || 'AI recommended this product but doctor did not include it.',
      });
    }
  }

  return divergences;
}

/**
 * Save a completed doc session to Firestore.
 */
export async function saveDocSession(session: DocSession): Promise<void> {
  const id = session.sessionId;

  await setDoc(doc(docSessionsCol(), id), {
    ...(stripUndefined(session) as Record<string, unknown>),
    environment: 'test',
    _createdAt: Timestamp.now(),
  });
}

/**
 * Compute alignment score: percentage of AI supplements that the doctor also selected.
 * Takes into account both product selection and dosage/timing agreement.
 */
export function computeAlignmentScore(
  doctorProtocol: DoctorProtocolItem[],
  aiProtocol: Protocol,
): number {
  const normalize = (s: string) => s.toLowerCase().trim();
  const aiNames = aiProtocol.supplements.map((s) => normalize(s.name));
  const docNames = new Set(doctorProtocol.map((d) => normalize(d.name)));

  if (aiNames.length === 0 && doctorProtocol.length === 0) return 100;

  const totalItems = new Set([...aiNames, ...docNames]).size;
  if (totalItems === 0) return 100;

  let matchScore = 0;

  for (const aiName of aiNames) {
    if (docNames.has(aiName)) {
      // Base match: product selected
      let itemScore = 0.6;

      // Bonus for matching dosage
      const docItem = doctorProtocol.find((d) => normalize(d.name) === aiName);
      const aiItem = aiProtocol.supplements.find((s) => normalize(s.name) === aiName);
      if (docItem && aiItem) {
        if (normalize(docItem.dosage) === normalize(aiItem.dosage)) {
          itemScore += 0.2;
        }
        if (docItem.timing === aiItem.timing) {
          itemScore += 0.2;
        }
      }

      matchScore += itemScore;
    }
  }

  // Doctor-only items are a slight penalty but not major
  const doctorOnlyCount = [...docNames].filter((n) => !aiNames.includes(n)).length;

  const maxPossible = totalItems;
  const raw = (matchScore / maxPossible) * 100;
  const penalty = doctorOnlyCount * 2; // Small penalty per extra item

  return Math.max(0, Math.min(100, Math.round(raw - penalty)));
}

// ============================================================
// DICTATION PARSING
// ============================================================

const DICTATION_MODEL = 'claude-sonnet-4-5-20250929';

interface ParsedItem {
  name: string;
  dosage: string;
  timing: 'AM_ENERGY' | 'MIDDAY_ABSORPTION' | 'PM_RECOVERY';
  productId: string | null;
  reasoning: string;
}

export interface ParsedDictation {
  items: DoctorProtocolItem[];
  clinicalNotes: string;
  offFormularyItems: string[];
}

/**
 * Parse a doctor's voice dictation into structured protocol items.
 * Uses Claude to extract treatment recommendations and match against
 * the Hemologix formulary.
 */
export async function parseDictation(
  transcript: string,
  biomarkers: BiomarkerResult[],
  demographics: { age: number; sex: 'female' | 'male' },
): Promise<ParsedDictation> {
  // Fetch formulary manifest for matching
  const manifest = await getManifest();
  const productList = manifest?.products?.map(
    (p) => `${p.id}: ${p.name} (${p.dosage}, ${p.timing}, tier ${p.tier})`
  ).join('\n') ?? 'No formulary loaded';

  const flaggedBiomarkers = biomarkers
    .filter((b) => b.hemologixStatus !== 'optimal')
    .map((b) => `${b.biomarker}: ${b.value} ${b.unit} [${b.hemologixStatus}]`)
    .join('\n');

  const systemPrompt = `You are a clinical NLP parser for HemoLogix. Extract treatment recommendations from a doctor's dictation.

FORMULARY (match to these products where possible):
${productList}

PATIENT:
- ${demographics.sex}, age ${demographics.age}
- Flagged biomarkers:
${flaggedBiomarkers}

RULES:
- Match each recommendation to a formulary product by ID when possible
- If the doctor mentions something generic (e.g. "vitamin D"), match to the closest formulary product
- If no formulary match exists, set productId to null
- Infer timing from context: morning/AM → AM_ENERGY, midday/afternoon → MIDDAY_ABSORPTION, evening/night/PM → PM_RECOVERY
- If timing not mentioned, default to AM_ENERGY
- Extract any clinical reasoning or general notes as clinicalNotes
- Return ONLY valid JSON, no markdown`;

  const userPrompt = `Doctor's dictation:
"${transcript}"

Return JSON:
{
  "items": [
    { "name": "Product Name", "dosage": "amount + unit", "timing": "AM_ENERGY|MIDDAY_ABSORPTION|PM_RECOVERY", "productId": "formulary-id or null", "reasoning": "why" }
  ],
  "clinicalNotes": "any general reasoning from the dictation"
}`;

  try {
    const raw = await claudeGenerate(DICTATION_MODEL, systemPrompt, userPrompt, 0, 2048);

    // Parse JSON from response
    const jsonMatch = raw.match(/\{[\s\S]*\}/);
    if (!jsonMatch) throw new Error('No JSON in response');

    const parsed = JSON.parse(jsonMatch[0]) as { items: ParsedItem[]; clinicalNotes: string };

    const offFormulary: string[] = [];
    const items: DoctorProtocolItem[] = parsed.items.map((item) => {
      if (!item.productId) offFormulary.push(item.name);

      return {
        productId: item.productId ?? `custom_${item.name.toLowerCase().replace(/\s+/g, '_')}`,
        name: item.name,
        dosage: item.dosage,
        timing: (['AM_ENERGY', 'MIDDAY_ABSORPTION', 'PM_RECOVERY'].includes(item.timing)
          ? item.timing
          : 'AM_ENERGY') as DoctorProtocolItem['timing'],
        notes: item.reasoning,
      };
    });

    return {
      items,
      clinicalNotes: parsed.clinicalNotes || '',
      offFormularyItems: offFormulary,
    };
  } catch (err) {
    console.error('parseDictation failed:', err);
    throw new Error('Could not parse dictation. Try rephrasing or typing your plan.');
  }
}
